export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    public details?: unknown,
    /**
     * `error.message` from the response envelope. The backend maps many
     * distinct causes onto a single code (leaderboard_invalid_tier_config
     * alone covers seven), so this is the only thing that tells them apart —
     * see errorReason() for how it's surfaced.
     */
    public serverMessage?: string,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

/**
 * The request never got an answer: the browser is offline, the connection
 * dropped, DNS failed, the host refused it, CORS blocked it. `fetch` rejects
 * with a bare `TypeError` for every one of these — the same type a bug in our
 * own code throws — so the transports wrap it here, and "the server said no"
 * (ApiError) is told apart from "the server was never reached" by type rather
 * than by the absence of one.
 *
 * What this error does NOT say is that nothing happened. The request may well
 * have been carried out and only the answer lost on the way back, so copy
 * written for it must not promise the operation was skipped.
 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super("Couldn't reach Social Hub API", { cause });
    this.name = "NetworkError";
  }
}

export const ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: "Email or password is incorrect.",
  rate_limited: "Too many attempts. Please try again later.",
  weak_password: "Password is too weak. Use at least 12 characters, with letters and digits.",
  invalid_token: "This link is invalid or expired. Ask the inviter to resend.",
  etag_mismatch: "Someone else updated this. Reload to see the latest version.",
  last_super_admin: "You can't do this — you would leave the platform without a super admin.",
  forbidden: "You don't have access to this resource.",
  not_found: "Resource not found.",
  cloud_save_conflict:
    "The save changed since this card was loaded. Reload to see it, then decide again.",
  cloud_save_too_large: "The file is larger than the project's cloud-save limit.",
  cloud_save_unavailable: "Cloud-save storage is not configured on this server.",
  cloud_save_object_gone:
    "The save's file is gone from the bucket; only its record is left. Delete the save here so the game starts the slot over.",
  cloud_save_not_wired: "Cloud saves are not wired on this server.",
  icon_too_large: "Icon must be 50KB or smaller.",
  already_member: "This user is already a member of the project.",
  csrf_invalid: "Your session expired. Please sign in again.",
  unauthenticated: "Please sign in to continue.",
  invalid_current: "Current password is incorrect.",
  cannot_disable_self: "You can't disable your own account.",
  invalid_name: "Name must be between 2 and 80 characters.",
  body_too_large: "Request too large.",
  invalid_bots_config:
    "The bot settings have fields the server refuses — see the list below.",
  // Only a tab that has been open since before the bot settings moved out of
  // games.config can produce this: it is still sending them inside the project
  // config, where nothing reads them any more. Saying so beats «something went
  // wrong», which would have the operator pressing Save again on a body the
  // server will refuse every time.
  bots_not_in_config:
    "The bot settings are saved on the Bots page now, not with the project config. Reload this page and try again.",
  // `bots.profile` left the Config page with the rest of the section — the
  // rules are on the Bots page now, and a message naming a screen that no
  // longer exists is an instruction nobody can carry out.
  no_profile_rules:
    "There are no profile rules to run. Set them on the Bots page → Source → Card rules first.",

  // Additional codes emitted by the backend (gap-audit Errors section).
  bad_game_id: "Invalid project ID.",
  bad_id: "Invalid ID.",
  bad_json: "Invalid request format.",
  bad_user_id: "Invalid user ID.",
  internal: "Something went wrong on our side. Please try again.",
  invalid_email: "Enter a valid email address.",
  mail_send_failed:
    "Couldn't send the invitation email. Check the SMTP settings and try again.",
  invalid_role: "Unknown role.",
  missing_game_id: "Project not specified.",
  // 400 from the players stat-key-coverage endpoint, whose whole input is the
  // `key` query param. Only a caller that forgot it ever sees this.
  missing_key: "No stat key was named.",
  precondition_required: "Missing precondition header. Reload and try again.",
  invalid_action: "Unknown activity action in the filter.",
  forbidden_origin: "Request blocked for security. Reload and try again.",
  bad_player_id: "Invalid player ID.",
  stats_not_object: "Stats must be a JSON object.",
  nothing_to_update: "No changes to save.",
  display_name_invalid: "Display name must be between 1 and 80 characters.",
  avatar_not_allowed: "That avatar is not in the project's allowed list.",
  invalid_stats: "Stats do not match the project's schema.",
  invalid_meta: "Meta does not match the project's schema.",
  meta_not_object: "Meta must be a JSON object.",

  // Phase 3.2 — guild & chat moderation codes.
  bad_guild_id: "Invalid guild ID.",
  bad_message_id: "Invalid message ID.",
  bad_help_request_id: "Invalid help request ID.",
  name_invalid: "Guild name must be between 1 and 64 characters.",
  description_invalid: "Description must be 512 characters or fewer.",
  visibility_invalid: "Visibility must be Open or Invite only.",
  metadata_invalid: "Metadata must be a JSON object.",
  role_invalid: "Unknown member role.",
  message_invalid: "Message is empty or too long.",
  invalid_role_transition: "That role change isn't possible — reload to see the current roles.",
  cannot_kick_leader: "You can't kick the guild leader. Transfer leadership first.",

  // Phase 5 — leaderboards codes.
  leaderboard_not_found: "Leaderboard not found",
  leaderboard_validation: "Invalid leaderboard configuration",
  leaderboard_score_not_found: "Score not found in this cycle",
  leaderboard_cycle_archived: "This cycle is archived; cannot modify",
  leaderboard_subscribe_denied: "Leaderboard does not exist or is in a different game",
  leaderboard_realtime_disabled: "Realtime is disabled for this leaderboard",

  // Remaining sentinels of internal/leaderboard/errors.go. Without these the
  // admin fell back to "Something went wrong" on every configuration 400.
  leaderboards_disabled: "Leaderboards are disabled for this project.",
  leaderboard_cycle_closed: "This cycle is closed — scores can no longer change.",
  leaderboard_submit_denied: "This leaderboard doesn't accept submits from that channel.",
  leaderboard_no_guild: "The player isn't in a guild, so there's nothing to rank.",
  leaderboard_permission_denied: "You don't have access to this leaderboard.",
  auth_invalid_token: "Your session expired. Please sign in again.",
  guard_max_score: "The score exceeds the max_score_per_submit guard.",
  guard_max_delta: "The score jump exceeds the max_delta_per_submit guard.",
  guard_interval: "Submits are arriving faster than min_interval_sec allows.",
  leaderboard_invalid_cron: "The cron expression is invalid.",
  leaderboard_immutable_field: "That field can't be changed after creation.",
  leaderboard_cap_reached: "This project has reached its leaderboard limit.",
  leaderboard_ext_key_taken: "A leaderboard with this ext key already exists.",
  validation_failed: "Some fields are invalid.",
  leaderboard_unknown_player: "Unknown player for this project.",
  leaderboard_event_not_started: "The event window hasn't started yet.",
  leaderboard_event_ended: "The event window has ended.",
  leaderboard_invalid_segmentation: "The segmentation setup is invalid.",
  // 403 on POST /submit — and on the two admin moderation calls, DELETE and
  // PATCH .../cycles/{cycle_id}/scores/{entity_id}. The board computes its own
  // scores from a players.stats key, so there is no incoming value for it to
  // accept from either door; the refusal is loud rather than a silent 200, so
  // a build still submitting is found.
  //
  // Both doors are named because the admin never submits: the moderation pair
  // is the only way an operator reads this line at all, and a text about
  // submits would answer an action nobody here performed.
  leaderboard_auto_scored:
    "This leaderboard's score is computed by the server from a player stat, so it accepts neither submits nor manual score edits.",
  leaderboard_invalid_tier_config: "The tier setup is invalid.",
  leaderboard_bucket_not_found: "Bucket not found.",
  reward_not_found: "Reward not found.",
  leaderboard_invalid_reward_range: "The reward range is invalid.",
  grant_not_found: "Reward grant not found.",
  grant_already_claimed: "This reward has already been claimed.",
  grant_expired: "The claim window for this reward has expired.",

  // Project snapshot transfer — the plan/apply endpoints. `body_too_large`
  // (over 5 MB) and `etag_mismatch` are already covered above.
  snapshot_bad_json:
    "This file isn't valid JSON. Pick the .snapshot.json file the export produced.",
  snapshot_unknown_schema:
    "This file isn't a project snapshot, or it comes from a newer version of Social Hub. Export it again from an environment running this version.",
  snapshot_empty:
    "This snapshot carries no sections. Export it again with at least one of config, leaderboards, bot phrases, the bot roster or the guild templates selected.",
  snapshot_invalid_config:
    "The snapshot's configuration has fields the server refuses — see the list below.",
  snapshot_invalid_leaderboard:
    "A leaderboard in this snapshot is not valid on this environment — see which one below.",
  snapshot_duplicate_key:
    "This snapshot names the same thing twice, so there is no telling which definition should win. Fix the file and load it again.",

  // Leaderboard rewards as a file — the export/plan/import endpoints.
  // `body_too_large` (over 1 MB here) is already covered above.
  rewards_bad_json:
    "This file isn't valid JSON. Pick the .json file the export produced.",
  rewards_unknown_schema:
    "This file isn't a rewards export, or it comes from a newer version of Social Hub. Export it again from an environment running this version.",
  rewards_invalid:
    "This file can't be applied to this leaderboard — see which rewards below.",
  rewards_too_many:
    "This file carries more rewards than one leaderboard can hold. Split it, or trim it to 500 rewards.",
  rewards_invalid_mode: "Unknown import mode — reload the page and try again.",
};

export function describeError(code: string): string {
  return ERROR_MESSAGES[code] ?? "Something went wrong. Please try again.";
}

/**
 * The backend's own explanation of the failure, for the line under the
 * human-readable text. The Go handler writes `err.Error()` into
 * `error.message`, which is "<code>: <reason>" — the code half is already
 * covered by describeError, so it's stripped. Returns null when there is
 * nothing worth adding.
 */
export function errorReason(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const raw = err.serverMessage?.trim();
  if (!raw) return null;
  const prefix = `${err.code}: `;
  const reason = raw.startsWith(prefix) ? raw.slice(prefix.length).trim() : raw;
  if (!reason || reason === err.code) return null;
  return reason;
}

/**
 * The itemised faults behind an error, for the codes whose copy promises a
 * list: `snapshot_invalid_config` carries `details.problems`, and
 * `snapshot_invalid_leaderboard` carries the offending `ext_key` and why.
 *
 * Returns an empty list rather than throwing on an unexpected shape — an error
 * screen that itself fails is the one thing worse than a vague error screen.
 */
export function errorProblems(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  const details = err.details;
  if (!details || typeof details !== "object") return [];

  const bag = details as Record<string, unknown>;
  if (Array.isArray(bag.problems)) {
    return bag.problems.filter((p): p is string => typeof p === "string");
  }
  if (typeof bag.ext_key === "string") {
    const reason = typeof bag.reason === "string" ? bag.reason : "";
    return [reason ? `${bag.ext_key} — ${reason}` : bag.ext_key];
  }
  const reason = errorReason(err);
  return reason ? [reason] : [];
}
